import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Section, Container } from 'components/shared';
import cx from 'classnames';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import HeartIcon from 'components/icons/Heart';
import HeartFilled from 'components/icons/HeartFilled';
import ShareIcon from 'components/icons/Share';
import MailIcon from 'components/icons/Mail';
import ChatIcon from 'components/icons/ChatBubble';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import { getMobileOS } from 'utils/getMobileOS';
import { useSpring, animated } from 'react-spring';
import ChevronDown from 'components/icons/ChevronDown';
import { AspectRatio } from 'react-aspect-ratio';
import { shareThis, getUrl } from 'utils/shareThis';
import useFavorites from 'utils/hooks/useFavorites';
import { useSitecoreContext } from 'stores/sitecoreContext';
import { track } from 'utils/tracking';
import trackingEvents from 'utils/trackingEvents';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';
import {
  videoTranscriptFileUrl,
  videoTranscriptFileExtension,
} from 'utils/videoTranscriptFileUrl';
import DownloadIcon from 'components/icons/Download';
import tw from 'twin.macro';
import {
  Document,
  Page,
  PDFDownloadLink,
  Image,
  BlobProvider,
} from '@react-pdf/renderer';
import config from 'temp/config';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';
import { ImageCarousel } from 'components/components/molecules';

const StyledSection = styled(Section)`
  padding-top: 0 !important;
  ${({ isMobile }) =>
    isMobile
      ? 'padding-bottom: 0 !important;'
      : 'padding-bottom: 0.625rem !important;'}
`;

const StaticCarouselContainer = styled.div`
  ${({ isMobile }) => !isMobile && 'height: auto;'}
`;

const Header = styled.div`
  height: 3.75rem;
  ${({ showNewSchemaColor, newSchemaColorHexaCode }) =>
    showNewSchemaColor ? `background-color: ${newSchemaColorHexaCode};` : ''}
`;

const IconContainer = styled.div`
  width: 2.25rem;
  height: 2.25rem;
`;

const StyledImageCarousel = styled(ImageCarousel)`
  ${tw`bg-mattamy-gray-bg md:rounded`}

  height: calc(100% - 3.75rem);
`;

const Template = ({ printMedia }) => {
  return (
    <Document>
      {printMedia.map((m, index) => {
        let texturl = '';
        let hasError = false;
        try {
          texturl = new URL(m.src);
        } catch (e) {
          texturl = m.src;
          hasError = true;
        }
        return (
          config && (
            <Page
              key={index}
              size='A4'
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Image
                style={{
                  marginTop: '15',
                  width: '90%',
                }}
                src={
                  hasError ? texturl : config.sitecoreApiHost + texturl.pathname
                }
              />
            </Page>
          )
        );
      })}
    </Document>
  );
};

const getFilterLabel = (filteredCtas, selectedFilter) => {
  const filter = filteredCtas.find((e) => e.key === selectedFilter);
  if (filter) return filter.label;
  return selectedFilter;
};

const filterCtas = (ctas, media) =>
  ctas.filter((cta) => {
    const itemIndex = media.findIndex(
      (i) =>
        i.type === cta.key ||
        (cta.key === 'video' && ['external', 'internal'].includes(i.type)),
    );
    return itemIndex !== -1;
  });

const printKeys = ['floorplan', 'planoption', 'keyplan'];

const StaticCarousel = ({ fields, rendering, messageFlag = '' }) => {
  const { showNewSchemaColor, newSchemaColorHexaCode } =
    useNewColorSchemaOptions();
  const { favoritesList, saveFavorite, deleteFavorite } = useFavorites();

  // const ctas = getFieldValue(fields, "ctas") || [];
  const ctas = [
    { key: 'image', text: 'Images' },
    { key: 'video', text: 'Videos' },
    { key: 'floorplan', text: 'Floorplans' },
    { key: 'planoption', text: 'Plan Options' },
    { key: 'keyplan', text: 'Key Plan' },
  ];
  const showFavorites = getFieldValue(fields, 'showFavorites');
  const showShare = getFieldValue(fields, 'showShare');
  const media = (getFieldValue(fields, 'media') || []).filter((el) => el.src);

  const { ContextID = {} } = useSitecoreContext() || {};

  const id = ContextID.value;

  const filteredCtas = filterCtas(ctas, media);

  const [selectedFilter, setSelectedFilter] = useState(
    filteredCtas[0] && filteredCtas[0].key,
  );
  const [isSharedVisible, setIsSharedVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const isIOS = getMobileOS() === 'iOS';
  const isLandscapeMode =
    typeof window !== 'undefined' &&
    window.orientation &&
    window.orientation === 90;

  const filteredMedia = media.filter(
    (item) =>
      !selectedFilter ||
      item.type === selectedFilter ||
      (selectedFilter === 'video' &&
        ['internal', 'external'].includes(item.type)),
  );
  const printMedia = media.filter((item) => printKeys.includes(item.type));
  const [isClient, setIsClient] = useState(false);

  const dropdown = useSpring({
    height: open && filteredCtas ? filteredCtas.length * 38 : 0,
    delay: open ? 200 : 0,
  });

  const shareDropdown = useSpring({
    height: isSharedVisible ? (isMobile || isLandscapeMode ? 88 : 46) : 0,
    delay: isSharedVisible ? 100 : 0,
  });

  const isFavorite = favoritesList.includes(id);

  const handleFavoriteClick = (id) => {
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };

  const { componentName } = rendering;

  const renderVideoDescription = () =>
    filteredMedia
      .filter((m) => m.type === 'internal' || m.type === 'external')
      .filter(
        (m) =>
          m.alt && m.videoAltTranscriptFile && m.videoAltTranscriptFile.link,
      )
      .map((m, i) => (
        <span
          key={i}
          className='richtext text-left text-md leading-7 font-graphie text-mattamy-blue'
        >
          {'Transcript: '}
          <a
            href={videoTranscriptFileUrl(m.videoAltTranscriptFile)}
            download={
              m.videoAltTranscriptFile.name +
              videoTranscriptFileExtension(m.videoAltTranscriptFile.extension)
            }
            className='underline'
            aria-label={'Download ' + m.alt + ' transcript'}
          >
            {m.alt}
          </a>
        </span>
      ));

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!media.length) return null;

  return (
    <>
      <StyledSection
        id={componentName}
        className='xl:px-20 select-none'
        isMobile={isMobile}
        tabIndex={'-1'}
      >
        <Container noPadding>
          <AspectRatio
            ratio={isMobile ? '375/452' : '1200/650'}
            className='w-full'
          >
            <StaticCarouselContainer className='w-full md:rounded-lg overflow-hidden relative'>
              <Header
                className={cx(
                  'h-20 md:rounded flex justify-center md:justify-between items-center px-5 mb-0 md:mb-4',
                  {
                    'bg-action-blue': !showNewSchemaColor,
                  },
                )}
                showNewSchemaColor={showNewSchemaColor}
                newSchemaColorHexaCode={newSchemaColorHexaCode}
              >
                {isClient && (
                  <div
                    className={cx(' w-1/4 lg:w-1/4', {
                      'md:relative md:flex md:ml-0 md:w-1/6 ': !isLandscapeMode,
                      'absolute left-0 ml-4': isMobile || isLandscapeMode,
                    })}
                  >
                    <BlobProvider
                      document={<Template printMedia={printMedia} />}
                    >
                      {({ blob, url, loading, error }) => {
                        return (
                          <a
                            href={url}
                            target={'_blank'}
                            aria-label={`Download ${selectedFilter}. Opens in a new tab`}
                            rel='noreferrer'
                          >
                            <DownloadIcon
                              title={`Download ${selectedFilter}. Opens in a new tab`}
                              className='flex uppercase text-white w-8 justify-center items-center text-sm font-trade-gothic-20 cursor-pointer'
                            />
                          </a>
                        );
                      }}
                    </BlobProvider>
                  </div>
                )}

                <div
                  className={cx('flex relative lg:w-1/2 flex justify-center', {
                    'w-full': isLandscapeMode,
                    'md:w-4/6 ': !isLandscapeMode,
                  })}
                >
                  {isMobile ? (
                    <>
                      <div
                        className='inline-block uppercase font-trade-gothic-20 text-white font-semibold cursor-pointer mt-1'
                        onClick={() => setOpen(!open)}
                      >
                        <span className='mr-2'>
                          {getFilterLabel(filteredCtas, selectedFilter)}
                        </span>
                        <ChevronDown
                          className={cx(
                            'inline-block w-10-mattamy mb-1 transition-all duration-500 transform',
                            {
                              '-rotate-180': open,
                            },
                          )}
                        />
                      </div>
                    </>
                  ) : (
                    filteredCtas &&
                    filteredCtas.map((item, i) => (
                      <>
                        <button
                          className={cx(
                            'uppercase leading-tight active:outline-none  font-trade-gothic-20 font-semibold',
                            {
                              'text-white': !showNewSchemaColor,
                              'text-black': showNewSchemaColor,
                            },
                          )}
                          key={item.text + i}
                          onClick={(e) => setSelectedFilter(item.key)}
                          aria-pressed={selectedFilter === item.key}
                          aria-label={`Show ${item.text}`}
                        >
                          <p
                            className={cx('pt-1', {
                              'border-b-2': selectedFilter === item.key,
                              'border-white': !showNewSchemaColor,
                              'border-black': showNewSchemaColor,
                            })}
                          >
                            {item.text}
                          </p>
                        </button>
                        {i < filteredCtas.length - 1 && (
                          <span
                            className={cx('mx-3 leading-10', {
                              'text-white': !showNewSchemaColor,
                              'text-black': showNewSchemaColor,
                            })}
                          >
                            |
                          </span>
                        )}
                      </>
                    ))
                  )}
                </div>
                <div
                  className={cx(
                    'absolute right-0  w-1/4  lg:w-1/4 flex justify-end items-center z-20',
                    {
                      'mr-8': isLandscapeMode,
                      'md:relative md:mr-0 md:mt-0 md:w-1/6': !isLandscapeMode,
                    },
                  )}
                >
                  {showFavorites && (
                    <div className='absolute right-0 mr-12 md:relative md:mr-0'>
                      <IconContainer
                        className='flex items-center justify-center rounded-full text-white mr-1 md:mr-10-mattamy hover:text-white cursor-pointer'
                        onClick={() => handleFavoriteClick(id)}
                        onMouseEnter={() => setFocused(true)}
                        onMouseLeave={() => setFocused(false)}
                        aria-label={
                          isFavorite ? 'Mark as unfavorite' : 'Mark as favorite'
                        }
                        aria-pressed={isFavorite ? 'true' : 'false'}
                        role='button'
                        tabIndex={0}
                      >
                        {isFavorite && <HeartFilled className='h-5 w-5' />}
                        {!isFavorite && focused && (
                          <HeartFilled className='h-5 w-5' />
                        )}
                        {!isFavorite && !focused && (
                          <HeartIcon className='h-5 w-5' />
                        )}
                      </IconContainer>
                    </div>
                  )}
                  {showShare && (
                    <div
                      onBlur={() => {
                        setTimeout(() => {
                          setIsSharedVisible(false);
                        }, 100);
                      }}
                      className='absolute right-0 mr-3 md:relative md:mr-0'
                    >
                      <IconContainer
                        className={cx(
                          'flex items-center justify-center rounded-full text-white hover:text-white  cursor-pointer',
                          {
                            'bg-mattamy-blue text-white':
                              !showNewSchemaColor && isSharedVisible,
                            'bg-black text-white':
                              showNewSchemaColor && isSharedVisible,
                          },
                        )}
                        tabIndex='0'
                        onClick={() => {
                          setIsSharedVisible(!isSharedVisible);
                        }}
                      >
                        <ShareIcon
                          className={cx('h-5', {
                            'text-white': isSharedVisible,
                          })}
                        />
                      </IconContainer>
                      {isSharedVisible && (
                        <animated.div
                          className='absolute z-20 w-full flex flex-col justify-center overflow-hidden'
                          style={shareDropdown}
                        >
                          <div
                            className='st-custom-button'
                            onClick={() => {
                              shareThis({
                                dataNetwork: 'email',
                                dataUrl: getUrl(),
                              });
                            }}
                          >
                            <IconContainer
                              data-aos='fade-down'
                              data-aos-duration='100'
                              className={cx(
                                'rounded-full mt-2 flex items-center justify-center ',
                                {
                                  'bg-mattamy-blue': !showNewSchemaColor,
                                  'bg-black': showNewSchemaColor,
                                },
                              )}
                            >
                              <MailIcon className='h-5 w-5 text-white' />
                            </IconContainer>
                          </div>
                          {(isMobile || isLandscapeMode) && (
                            <a
                              href={`sms:${
                                isIOS ? '&' : '?'
                              }body=${encodeURIComponent(getUrl())}`}
                              onClick={() => {
                                track({ eventId: trackingEvents.ClickToText });
                                trackAnalyticsEvent({
                                  gtagEvent: 'ShareClick',
                                  mouseflowTag: 'Share',
                                });
                              }}
                              aria-label={'Share by text message'}
                            >
                              <IconContainer
                                data-aos='fade-down'
                                data-aos-duration='100'
                                data-aos-delay='50'
                                className={cx(
                                  'rounded-full mt-2 flex items-center justify-center ',
                                  {
                                    'bg-mattamy-blue': !showNewSchemaColor,
                                    'bg-black': showNewSchemaColor,
                                  },
                                )}
                              >
                                <ChatIcon className='h-5 w-5 text-white' />
                              </IconContainer>
                            </a>
                          )}
                        </animated.div>
                      )}
                    </div>
                  )}
                </div>
              </Header>

              {isMobile && (
                <animated.div
                  className='absolute z-20 w-full bg-mattamy-blue flex flex-col justify-center overflow-auto'
                  style={dropdown}
                >
                  {filteredCtas &&
                    filteredCtas.map((item, i) => (
                      <button
                        className={cx(
                          'uppercase tracking-tight leading-none active:outline-none  font-trade-gothic-20 text-white font-semibold pt-1 my-2',
                        )}
                        key={item.text + i}
                        onClick={(e) => {
                          setSelectedFilter(item.key);
                          setOpen(false);
                        }}
                      >
                        {item.text}
                      </button>
                    ))}
                </animated.div>
              )}
              {isMobile && messageFlag && (
                <div className='h-10 w-full flex justify-center items-center text-promo-dark-blue bg-aqua border-promo-dark-blue border-2'>
                  <p className='h-4 text-sm md:text-base font-trade-gothic-20 uppercase'>
                    {messageFlag}
                  </p>
                </div>
              )}
              {filteredMedia && (
                <StyledImageCarousel
                  isMobile={isMobile}
                  carousel={filteredMedia}
                  fullWidth
                  counterRight={!isMobile}
                  withTopBar
                  noArrows={isMobile}
                  isContainerRounded={false}
                  plansStyle
                  hasZoomIcons={true && !isLandscapeMode}
                  showNewSchemaColor={showNewSchemaColor}
                  messageFlag={messageFlag}
                  mobileAspectRatio='375/452'
                  desktopAspectRatio='1200/650'
                />
              )}
            </StaticCarouselContainer>
          </AspectRatio>
          {filteredMedia && (
            <div className='w-full mt-2 flex flex-col'>
              {renderVideoDescription()}
            </div>
          )}
        </Container>
      </StyledSection>
    </>
  );
};

export default StaticCarousel;
